import classNames from 'classnames';
import React, { CSSProperties, ReactNode, useEffect, useRef, useState } from 'react';

import ChangeZipCode from '@/components/common/ChangeZipCode';
import Header from '@/components/common/HeaderNew';
import GlobalNavHeader, { globalNavHeaderUnstickedHeight } from '@/components/common/GlobalNavHeader';
import TopCommonPromoBar from '@/components/landing/components/TopCommonPromoBar';
import usePopUpDeleteQuery from '@/hooks/usePopUpDeleteQuery';
import { SEOInfo } from '@/types/seo';
import { getZipcode } from '@/utils/common/porder';
import { isSayWeeeApp } from '@/utils/common/device';
import { SearchOverlayProps } from '@/components/common/SearchOverlay';

import { twMerge } from 'tailwind-merge';
import useSticky from '@/hooks/useSticky';
import PageNavHeader from '@/components/common/EnkiDummyUI/PageNavHeader';
import useGlobalNavHeader from '@/hooks/globalnavheader/provider';
import useScrollToAddBoxShadow from '@/hooks/useScrollToAddBoxShadow';
import useTranslation from 'next-translate/useTranslation';

export enum pageMode {
  subPage = 'subPage'
}

export interface Props {
  style?: CSSProperties;
  className?: string;
  headerWrapClassName?: string;
  headerClassName?: string;
  title?: string | React.ReactNode;
  contentClassName?: string;
  contentStyle?: CSSProperties;
  children?: ReactNode;
  onShare?: (any) => void;
  shareData?: ObjectType;
  onBack?: () => string;
  topNode?: React.ReactNode;
  header?: React.ReactNode | boolean;
  hasNavigation?: boolean;
  hasRecommendBanner?: boolean;
  seoInfo?: SEOInfo;
  canonicalLink?: string;
  topNotification?: React.ReactNode;
  onSearch?: (any) => string;
  showSearchInput?: boolean;
  toSearchPage?: boolean;
  showSearchIcon?: boolean;
  /**
   * 是否展示12小时倒计时banner在用户首次访问的是pdp页面
   */
  showTimeBanner?: boolean;
  //是否默认微信分享信息
  isServerSide?: boolean;
  shareIcon?: string;
  shareAfterClose?: () => void;
  trackSharePopupClick?: (e) => void;
  onBeforeShareShow?: (modal: any) => void;
  noCheckZipcode?: boolean;
  leftNode?: React.ReactNode;
  rightNode?: React.ReactNode;
  showBackHomeIcon?: boolean;
  headerBottomSlot?: React.ReactNode;
  contentBottomSlot?: React.ReactNode;
  timeBannerProps?: ObjectType;
  statusBarColor?: string;
  //在服务端渲染时, 如果能拿到url上的参数, 可以优化状态栏等
  appStatusBarHeight?: number;
  stickyParams?: {
    custom?: boolean;
    scrollHeight?: number;
    targetRef?: any;
    cb?: (sticky?: boolean) => void;
  };
  headerBoxClass?: string;
  blockStreamlinedHeader?: boolean;
  // @streamlinedHeaderIsSearchMode: When true, GlobalNavHeader takes over the responsability of rendering <SearchOverlay> (instead of page level)
  streamlinedHeaderIsSearchMode?: boolean;
  // @searchTrigger: Page level delegates a callback. E.g: /search , /mkpl/search propagate the callback down the layout since business logic pertains to the page.
  searchTrigger?: (keyword: string, trigger_type?: string) => void;
  searchOverlayProps?: SearchOverlayProps;
  /** 点击搜索框进入 /search 时携带的额外 query，如 { filters: '{"delivery_type":"delivery_type_pantry"}' } */
  customSearchQuery?: Record<string, string>;
  openDrawer?: () => void;
  canUsePageNavHeader?: boolean;
  skipShadowForRoute?: boolean;
  skipSubSectionStickyForRoute?: boolean;
  headerHasDivider?: boolean;
  showCategoriesPastRandomScroll?: boolean;
  categoryRowShowsPastElementScrollRef?: React.MutableRefObject<HTMLElement>;
  useSearchModal?: ObjectType;
  dataTestId?: string;
  customThirdLevel?: React.ReactNode;
  showStore?: boolean;
}

export default function Layout(props: Props) {
  const globalNavHeader = useGlobalNavHeader();

  const zipcodeRef = useRef(null);

  const {
    style,
    className,
    headerClassName,
    headerWrapClassName,
    contentClassName,
    contentStyle,
    children,
    header,
    topNode,
    hasNavigation = false,
    hasRecommendBanner = false,
    canonicalLink,
    noCheckZipcode = false,
    showTimeBanner,
    headerBottomSlot,
    contentBottomSlot,
    timeBannerProps,
    statusBarColor,
    appStatusBarHeight,
    headerBoxClass,
    blockStreamlinedHeader,
    streamlinedHeaderIsSearchMode,
    searchTrigger,
    searchOverlayProps,
    customSearchQuery,
    openDrawer,
    canUsePageNavHeader,
    skipShadowForRoute,
    skipSubSectionStickyForRoute,
    headerHasDivider = true,
    useSearchModal,
    dataTestId,
    showStore = true,
    ...rest
  } = props;
  const { t } = useTranslation('common');
  const [statusBarHeight] = useState(appStatusBarHeight);

  const headerRef = useRef(null);
  const streamlinedHeaderPromoBannerRef = useRef(null);

  const { custom = true, targetRef = headerRef, ...others } = props.stickyParams || {};

  const { eleRef } = useSticky({
    targetRef,
    scrollHeight: showTimeBanner ? 102 : 47,
    skipSubSectionStickyForRoute: skipSubSectionStickyForRoute,
    ...others
  });

  useEffect(() => {
    const zipcode = getZipcode();
    if (!zipcode && !isSayWeeeApp()) {
      zipcodeRef.current?.show();
    }
  }, []);

  usePopUpDeleteQuery();
  const streamlinedHeader = blockStreamlinedHeader ? false : globalNavHeader?.streamlinedHeader;
  const headerNavSizesConfig = globalNavHeader?.headerNavSizesConfig;

  const __renderTimeBanner = () => {
    return (
      showTimeBanner && (
        <TopCommonPromoBar
          {...timeBannerProps}
          cookieKey="pdp_landing_stamp_expires"
          _subRef={streamlinedHeaderPromoBannerRef}
        />
      )
    );
  };

  const navHeaderRef = useRef(null);
  const categoryRowDynamicScrollRef = useRef(null);
  const showCategoriesPastRandomScroll = props?.showCategoriesPastRandomScroll || false;
  const categoryRowShowsPastElementScrollRef = props?.categoryRowShowsPastElementScrollRef || undefined;

  const boxShadowHook = useScrollToAddBoxShadow({
    targetRef: navHeaderRef,
    skipCompletely: !streamlinedHeader,
    streamlinedHeader: streamlinedHeader,
    headerSizesConfig: headerNavSizesConfig,
    promoBannerRef: streamlinedHeaderPromoBannerRef,
    skipShadowForRoute: skipShadowForRoute,
    showCategoriesPastRandomScroll,
    categoryRowDynamicScrollRef,
    categoryRowShowsPastElementScrollRef
  });

  return (
    <div
      data-testid={dataTestId}
      className={twMerge(
        classNames(
          'md:w-[750px] md:mx-auto md:my-0',
          {
            ['pb-16']: hasNavigation
          },
          className
        )
      )}
      style={style}>
      {!noCheckZipcode && <ChangeZipCode ref={zipcodeRef} />}
      <div
        className={twMerge(
          'sticky z-[302]',
          streamlinedHeader ? globalNavHeaderUnstickedHeight : 'top-0',
          !streamlinedHeader ? headerBoxClass : 'top-0'
        )}
        id="layout-header"
        ref={headerRef}>
        {statusBarHeight > 0 && <div style={{ height: statusBarHeight, backgroundColor: statusBarColor }} />}
        {__renderTimeBanner()}
        {!streamlinedHeader && topNode}
        {!streamlinedHeader && header !== false && (
          <div
            className={twMerge(
              classNames(
                'flex items-center justify-center h-12 max-w-[750px] md:w-[750px] md:mx-auto md:my-0',
                headerWrapClassName
              )
            )}>
            {header ? (
              header
            ) : (
              <Header
                {...rest}
                className={twMerge(
                  classNames(
                    { ['!bg-[#49c468]']: (process.env.ENV_VAR || process.env.NEXT_PUBLIC_ENV_VAR) === 'test' },
                    headerClassName
                  )
                )}
              />
            )}
          </div>
        )}
        {streamlinedHeader && (
          <GlobalNavHeader
            customThirdLevel={props?.customThirdLevel}
            isSearchMode={streamlinedHeaderIsSearchMode}
            searchTrigger={searchTrigger}
            searchOverlayProps={{
              ...searchOverlayProps,
              ...(customSearchQuery && { customSearchQuery })
            }}
            observedStickyRef={navHeaderRef}
            categoryRowDynamicScrollRef={categoryRowDynamicScrollRef}
            showCategoriesPastRandomScroll={showCategoriesPastRandomScroll}
            hasDivider={headerHasDivider}
            useSearchModal={useSearchModal}
            showStore={showStore}
          />
        )}
        {!!headerBottomSlot && headerBottomSlot}
      </div>
      {!!canUsePageNavHeader && (
        <PageNavHeader currentPageTitle={props?.title} backTitle={t('generic_back_nav_short')} />
      )}
      {!!children && (
        <div
          ref={boxShadowHook.eleRef}
          className={twMerge(
            classNames(contentClassName, {
              // ['pb-16']: hasNavigation
            })
          )}
          style={contentStyle}
          data-role="content">
          {!custom && <div ref={eleRef} />}
          {children}
        </div>
      )}
      {!!contentBottomSlot && contentBottomSlot}
    </div>
  );
}
